
import { MapPin, Sprout, Star } from "lucide-react";

interface Farm {
  id: number;
  name: string;
  username: string;
  avatar: string;
  cover: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  crops: string[];
  distance: string;
  description: string;
  verified: boolean;
  category: string;
  calculatedDistance?: number;
  plantHardinessZone: string;
  userId?: string;
}

interface DiscoverFarmCardProps {
  farm: Farm;
  onVisit: (farm: Farm) => void;
  isLiteMode?: boolean;
}

export const DiscoverFarmCard = ({ farm, onVisit, isLiteMode = false }: DiscoverFarmCardProps) => {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
      {/* Compact Cover Image */}
      <div className="bg-gradient-to-r from-green-400 to-blue-500 relative h-20 sm:h-24">
        <img
          src={farm.cover}
          alt="Farm cover"
          className="w-full h-full object-cover"
        />
        {farm.verified && (
          <div className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5">
            <Star size={10} className="text-yellow-500" />
          </div>
        )}
      </div>
      
      {/* Compact Farm Content */}
      <div className="p-2">
        {/* Header with Avatar */}
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-6 h-6 rounded-full border border-green-200 overflow-hidden flex-shrink-0">
            <img 
              src={farm.avatar} 
              alt={`${farm.name}'s avatar`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-800 text-xs truncate">
              {farm.name}
            </h3>
            <p className="text-gray-500 text-xs">@{farm.username}</p>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center space-x-1 text-gray-600 text-xs mb-2">
          <MapPin size={8} />
          <span className="truncate flex-1">{farm.location.address}</span>
        </div>
        
        {/* Zone and Distance */}
        <div className="flex items-center justify-between mb-2">
          <div className="bg-green-600 text-white rounded px-1.5 py-0.5 flex items-center space-x-1">
            <span className="text-xs font-medium">{farm.plantHardinessZone}</span>
          </div>
          <span className="text-gray-500 text-xs">{farm.distance}</span>
        </div>

        {/* Main Crops */}
        <div className="flex flex-wrap gap-1 mb-2">
          {farm.crops.slice(0, 2).map((crop) => (
            <span
              key={crop}
              className="px-1.5 py-0.5 bg-green-50 text-green-700 text-xs rounded-full"
            >
              {crop}
            </span>
          ))}
        </div>

        {/* Compact Visit Button */}
        <button 
          onClick={() => onVisit(farm)}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-1.5 rounded-md text-xs font-medium transition-colors"
        >
          Visit Farm
        </button>
      </div>
    </div>
  );
};
